import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';

// Site nav (9/2) — the destinations had grown past what a corner of small
// links could carry: Film Clubs and Outdoor were buttons floating by the
// filters, 35mm was buried in the footer, the newsletter was 3,000px down the
// page. One row under the masthead in the site's own Art Deco language (Cinzel
// small caps, gold rules) rather than a generic app bar.
//
// Scrolls horizontally on narrow screens instead of collapsing to a hamburger:
// seven short items fit a swipe, and a hidden menu would bury the very links
// this exists to surface.
const LINKS = [
  { label: 'Home', to: '/' },                    // list view — the default
  { label: 'Calendar', to: '/?view=calendar' },  // grid: month + week
  { label: 'Clubs & Community Screenings', to: '/film-clubs' },
  { label: 'Venues', to: '/venues' },
  { label: 'Newsletter', to: '/#newsletter', hash: 'newsletter' },
  { label: 'Support the Site', to: '/support' },
  { label: 'FAQ', to: '/faq' },
];

export default function NavBar() {
  const { pathname, search } = useLocation();
  const { user, watchlist } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  // Mobile: a hamburger instead of a sideways-scrolling row (9/2, Josh). The
  // row survives on desktop where there is room for it.
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);
  const isActive = (item) => {
    if (!item.to) return false;
    // An anchor link ("/#newsletter") is a scroll target, never a section —
    // and its path reduces to "/", which every route starts with, so without
    // this it underlines itself on every page (9/2 QA).
    if (item.hash) return false;
    const [path, qs] = item.to.split('?');
    if (path !== '/') return pathname.startsWith(path);
    if (pathname !== '/') return false;
    // Home and Calendar share "/" — the view param separates them.
    const grid = search.includes('view=calendar') || search.includes('view=week');
    return qs ? grid : !grid;
  };

  return (
    <nav className="site-nav" aria-label="Main">
      <button
        type="button"
        className="site-nav-toggle"
        aria-expanded={menuOpen}
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        onClick={() => setMenuOpen((v) => !v)}
      >
        <span className="site-nav-bars" aria-hidden="true">
          <span /><span /><span />
        </span>
        Menu
      </button>
      <ul className={`site-nav-list${menuOpen ? ' is-open' : ''}`}>
        {LINKS.map((item) => {
          const active = isActive(item);
          const cls = `site-nav-link${active ? ' is-active' : ''}`;
          return (
            <li key={item.label}>
              {item.href ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cls}
                  onClick={() => {
                    closeMenu();
                    if (window.gtag) window.gtag('event', item.ga || 'nav_click', { destination: 'etsy', location: 'nav', transport_type: 'beacon' });
                  }}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  to={item.to}
                  className={cls}
                  onClick={(e) => {
                    // A hash link does nothing when the target is already on
                    // screen (no remount, no browser scroll) — scroll it here.
                    if (item.hash) {
                      const el = document.getElementById(item.hash);
                      if (el) {
                        e.preventDefault();
                        // Scroll AFTER the mobile menu collapses (9/2 QA:
                        // "newsletter overshoots") — measuring while the open
                        // menu still occupies ~300px lands well past the form.
                        setTimeout(() => {
                          const target = document.getElementById(item.hash);
                          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 60);
                        window.history.replaceState(null, '', item.to);
                      }
                    }
                    closeMenu();
                    if (window.gtag) window.gtag('event', 'nav_click', { link: item.label, transport_type: 'beacon' });
                  }}
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
        {/* Account sits at the right end of the nav (9/2) — it was stranded
            alone in the masthead corner. */}
        <li className="site-nav-account">
          {user ? (
            <>
              <Link to="/watchlist" className="site-nav-link" onClick={closeMenu}>
                Watchlist{watchlist.length > 0 ? ` (${watchlist.length})` : ''}
              </Link>
              <Link to="/profile" className="site-nav-link" onClick={closeMenu}>{user.email?.split('@')[0]}</Link>
            </>
          ) : (
            <button type="button" className="site-nav-link site-nav-signin" onClick={() => { closeMenu(); setShowAuth(true); }}>
              Sign In
            </button>
          )}
        </li>
      </ul>
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </nav>
  );
}
